import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useId, useRef, useState, type FormEvent } from "react";
import { Link, useLocation, useSearch } from "wouter";
import { api, ApiError } from "../api";
import { useCan } from "../auth/session";
import { Card, EmptyState, Field, InlineAlert, MoreMenu, PageHeader, PageShell, icons, useToast } from "../company-ui";
import { Tip } from "../help/Tip";
import { BandEditor } from "./BandEditor";

/**
 * Templates (ALN-35, docs/pages/templates.md), laid out as the mockup's v25 templates view, with
 * v29's "Priced in" (H11) when the winery has more than one company that can hold lots.
 * Standard cost per case reads "Not costed yet" until the plan calculation lands with the Program
 * builder (Ryan, Oct 18 2026). "Continue building" and a new draft open the builder, which isn't
 * built yet.
 */
export interface TemplateDto {
  id: string; name: string; wineType: string; agingRoute: string; fruitSource: string | null; status: "draft" | "saved"; origin: "program" | "starter" | "built" | "duplicate";
  stages: string[]; base: { tons: number | null; acres: number | null; tonsPerAcre: number | null; cases: number | null };
  standardCostPerCaseCents: number | null; standardYieldGalPerTon: number | null; sourceProgramName: string | null; createdByName: string | null;
  mediansAppliedAt: string | null; savedAt: string | null; archivedAt: string | null; archivedByName: string | null; createdAt?: string;
  summary: { activities: number; S: number; M: number; L: number; fromTeam: number; noHours: number };
  pricingCompany: { id: string; name: string } | null;
}
/** Whether New template asks for "Priced in", the person's companies for it, and its default. */
export interface PricingDto { show: boolean; defaultCompanyId: string | null; wineryCompanyId: string | null; companies: { id: string; name: string }[] }

const SUBTITLE = "Wines you've planned before, saved with their route, yields, activities, sizes and hours, so a new program starts from real numbers. A new program plans at today's rates for its own months.";
const STAGE_LABEL: Record<string, string> = { sourcing: "Sourcing", farming: "Farming", receiving: "Receiving", crush_press: "Crush and press", fermentation: "Fermentation", aging: "Aging", blending: "Blending", bottling: "Bottling", warehouse: "Warehouse" };
const WINE_LABEL: Record<string, string> = { red: "Red", white: "White", rose: "Rosé", sparkling: "Sparkling", dessert: "Dessert", other: "Other" };
const ROUTE_LABEL: Record<string, string> = { none: "None", stainless: "Stainless", barrel: "Barrel" };
const SIZE_NAME = { S: "Small", M: "Medium", L: "Large" } as const;
const message = (e: unknown) => (e instanceof ApiError ? e.message : "Something went wrong. Check your connection and try again.");
const day = (iso: string) => new Date(iso).toLocaleDateString(undefined, { month: "short", day: "numeric" });
const fmt = (n: number) => n.toLocaleString(undefined, { maximumFractionDigits: 3 });
const money = (cents: number) => (cents / 100).toLocaleString(undefined, { style: "currency", currency: "USD" });

export function TemplatesPage() {
  const canSee = useCan("costs.view");
  const canPlan = useCan("programs.plan");
  const search = useSearch();
  const focusId = new URLSearchParams(search).get("template");
  // "Edit sizes" for an activity opens the band editor above the grid (packet B)
  const sizesKey = new URLSearchParams(search).get("sizes");
  const [, navigate] = useLocation();
  const toast = useToast();
  const [archived, setArchived] = useState(false);
  const [creating, setCreating] = useState(false);
  const list = useQuery({ queryKey: ["templates", archived], queryFn: () => api<{ templates: TemplateDto[]; pricing: PricingDto }>(`/templates?archived=${archived}`), enabled: canSee });

  // a search result opens Templates on its card, archived or not
  const target = useQuery({ queryKey: ["template", focusId], queryFn: () => api<{ template: TemplateDto }>(`/templates/${focusId}`), enabled: canSee && !!focusId });
  useEffect(() => { if (target.data) setArchived(!!target.data.template.archivedAt); }, [target.data]);
  useEffect(() => {
    if (!focusId || !list.data) return;
    const el = document.getElementById(`tpl-${focusId}`);
    if (el) { el.scrollIntoView({ block: "center" }); el.focus({ preventScroll: true }); }
  }, [focusId, list.data]);

  if (!canSee) {
    return (
      <PageShell>
        <PageHeader title="Templates" subtitle={SUBTITLE} />
        <InlineAlert variant="info" title="You don't have access to templates. Ask an owner or controller." />
      </PageShell>
    );
  }

  const templates = list.data?.templates ?? [];
  const pricing = list.data?.pricing ?? null;
  return (
    <PageShell>
      <PageHeader title="Templates" subtitle={SUBTITLE} actions={
        <>
          <label className="tplhead-toggle"><input type="checkbox" checked={archived} onChange={e => setArchived(e.target.checked)} /><span>Show archived</span></label>
          {canPlan && <button type="button" className="btn btn--primary" aria-expanded={creating} onClick={() => setCreating(c => !c)}>{icons.plus}New template</button>}
        </>
      } />
      <div className="stack">
        {creating && <NewTemplateForm pricing={pricing} onCancel={() => setCreating(false)} />}
        {canPlan && sizesKey && <BandEditor key={sizesKey} activityKey={sizesKey} toast={toast} onClose={() => navigate("/templates")} />}
        {list.isPending ? (
          <div className="tplgrid" aria-busy="true" aria-label="Loading templates">
            {[0, 1, 2].map(i => <div key={i} className="card tplskel" data-testid="template-skeleton"><i /><i /><i /><i /></div>)}
          </div>
        ) : list.isError ? (
          <Card>
            <EmptyState icon={icons.warning} title="Templates couldn't load. Try again in a minute; nothing was changed."
              action={<button type="button" className="btn" onClick={() => void list.refetch()}>Retry</button>} />
          </Card>
        ) : templates.length === 0 ? (
          <Card>
            {archived
              ? <EmptyState title="No archived templates." />
              : <EmptyState title="No templates yet." description="Forward Rise support can load the starter templates for white, rosé, red and sparkling. Or build one with New template, or open a program and choose Save as template."
                  action={<Link className="btn btn--primary" href="/programs">Go to Programs</Link>} />}
          </Card>
        ) : (
          <div className="tplgrid">{templates.map(t => <TemplateCard key={t.id} t={t} canPlan={canPlan} pricing={pricing} />)}</div>
        )}
      </div>
    </PageShell>
  );
}

function activityLine(t: TemplateDto): string | null {
  const s = t.summary;
  if (!s.activities) return null;
  const sizes = (["S", "M", "L"] as const).filter(z => s[z]).map(z => `${s[z]} ${SIZE_NAME[z]}`).join(", ");
  const head = `${s.activities} ${s.activities === 1 ? "activity" : "activities"}: ${sizes}.`;
  return t.origin === "starter" && s.fromTeam === 0 ? `${head} Hours from template until the team's estimates close.` : `${head} ${s.fromTeam} from team estimates.`;
}

function baseLine(t: TemplateDto): string | null {
  const { tons, acres, tonsPerAcre, cases } = t.base;
  const c = cases != null ? `${fmt(cases)} cases` : null;
  if (acres != null && tonsPerAcre != null) return `Base: ${fmt(acres)} acres at ${fmt(tonsPerAcre)} tons an acre${c ? `, ${c}` : ""}`;
  if (tons != null) return `Base: ${fmt(tons)} tons${c ? `, ${c}` : ""}`;
  return c ? `Base: ${c}` : null;
}

function originLine(t: TemplateDto): string {
  if (t.origin === "starter") return "From the starter set";
  if (t.origin === "program") return t.sourceProgramName ? `From ${t.sourceProgramName}` : "From a program";
  return `Built by ${t.createdByName ?? "Avero support"}${t.createdAt ? `, ${day(t.createdAt)}` : ""}`;
}

type Mode = { kind: "idle" } | { kind: "rename" } | { kind: "archive" } | { kind: "delete" };

function TemplateCard({ t, canPlan, pricing }: { t: TemplateDto; canPlan: boolean; pricing: PricingDto | null }) {
  const qc = useQueryClient();
  const toast = useToast();
  const [, navigate] = useLocation();
  const headingId = useId();
  const [mode, setMode] = useState<Mode>({ kind: "idle" });
  const [error, setError] = useState<string | null>(null);
  const draft = t.status === "draft";
  const refresh = () => qc.invalidateQueries({ queryKey: ["templates"] });
  const act = useMutation({
    mutationFn: ({ path, method, body }: { path: string; method?: string; body?: object }) => api<{ template?: { id: string } }>(path, { method, body: body ?? {} }),
    onError: e => setError(message(e)),
  });
  const run = async (path: string, done: string, opts: { method?: string; body?: object } = {}) => {
    setError(null);
    const r = await act.mutateAsync({ path, ...opts }).catch(() => null);
    if (r === null) return null;
    setMode({ kind: "idle" });
    toast(done);
    void refresh();
    return r ?? {};
  };
  const yieldText = t.standardYieldGalPerTon != null ? `${fmt(t.standardYieldGalPerTon)} gal/ton` : draft ? "When saved" : "Not set";
  const cost = t.standardCostPerCaseCents != null
    ? <b>{money(t.standardCostPerCaseCents)}</b>
    : draft ? <span className="muted">When saved</span>
    : <span className="small">Not costed yet. Set rates in <Link href="/rates">Rates and pools</Link>.</span>;
  const activities = activityLine(t);
  const base = baseLine(t);
  const chip = draft ? "Draft" : t.origin === "starter" ? "Starter" : null;
  const priced = pricing?.show ? t.pricingCompany : null;
  const elsewhere = priced && priced.id !== pricing?.wineryCompanyId;

  return (
    <article className={`card tpl${t.archivedAt ? " tpl--archived" : ""}`} id={`tpl-${t.id}`} tabIndex={-1} aria-labelledby={headingId}>
      <div className="card__h">
        <div>
          <span className="kind">{WINE_LABEL[t.wineType] ?? t.wineType}</span>
          <h2 className="card__t" id={headingId}>{t.name}</h2>
          <p className="card__d">{originLine(t)}</p>
        </div>
        {chip && <span className="nowrap"><span className="chip chip--neutral chip--quiet">{chip}</span>{chip === "Starter" && <Tip term="Starter template" />}</span>}
      </div>
      <div className="tpl__body">
        <ul className="tplroute" aria-label="Stages">{t.stages.map(s => <li key={s}>{STAGE_LABEL[s] ?? s}</li>)}</ul>
        <dl className="tplmeta">
          <div><dt>Standard cost per case<Tip term="Standard cost per case" /></dt><dd>{cost}</dd></div>
          <div><dt>Standard yield<Tip term="Standard yield" /></dt><dd><b>{yieldText}</b></dd></div>
          {priced && <div><dt>Priced in<Tip term="Priced in" /></dt><dd><b>{priced.name}</b></dd></div>}
        </dl>
        {elsewhere && <p className="tplnote">Standard cost per case uses {priced.name}'s rates.</p>}
        {activities && <p className="tplnote">{activities}</p>}
        {base && <p className="tplnote">{base}<Tip term="Base size" /></p>}
        {t.mediansAppliedAt && <p className="tplnote">Hours updated from team estimates on {day(t.mediansAppliedAt)}.</p>}
        {t.archivedAt && <p className="tplnote">Archived {day(t.archivedAt)} by {t.archivedByName ?? "Avero support"}</p>}
        {error && mode.kind === "idle" && <p className="field__error" role="alert">{error}</p>}
        {canPlan && (
          <div className="tpl__foot">
            {mode.kind === "rename" ? (
              <RenameForm t={t} busy={act.isPending} error={error} onCancel={() => { setMode({ kind: "idle" }); setError(null); }}
                onSave={name => void run(`/templates/${t.id}`, `Renamed to ${name}.`, { method: "PATCH", body: { name } })} />
            ) : mode.kind === "archive" ? (
              <div className="stackgap stackgap--sm">
                <p className="small tplask"><b>Archive {t.name}?</b> Programs already started from it keep their plans.</p>
                <div className="actions">
                  <button type="button" className="btn btn--sm btn--destructive" disabled={act.isPending} onClick={() => void run(`/templates/${t.id}/archive`, `${t.name} archived.`)}>Archive</button>
                  <button type="button" className="btn btn--sm" onClick={() => setMode({ kind: "idle" })}>Cancel</button>
                </div>
              </div>
            ) : mode.kind === "delete" ? (
              <div className="stackgap stackgap--sm">
                <p className="small tplask"><b>Delete the draft {t.name}?</b> Nothing else uses it.</p>
                <div className="actions">
                  <button type="button" className="btn btn--sm btn--destructive" disabled={act.isPending} onClick={() => void run(`/templates/${t.id}`, `Draft ${t.name} deleted.`, { method: "DELETE" })}>Delete draft</button>
                  <button type="button" className="btn btn--sm" onClick={() => setMode({ kind: "idle" })}>Cancel</button>
                </div>
              </div>
            ) : t.archivedAt ? (
              <div className="actions"><button type="button" className="btn btn--sm" disabled={act.isPending} onClick={() => void run(`/templates/${t.id}/restore`, `${t.name} restored.`)}>Restore</button></div>
            ) : draft ? (
              <div className="actions">
                <Link className="btn btn--sm btn--primary" href={`/builder?template=${t.id}`}>Continue building</Link>
                <button type="button" className="btn btn--sm" onClick={() => setMode({ kind: "delete" })}>Delete draft</button>
              </div>
            ) : (
              <div className="actions">
                <button type="button" className="btn" onClick={() => navigate(`/programs?new=1&template=${t.id}`)}>Start a program from this</button>
                <MoreMenu className="tplmore" name={t.name} items={[
                  { label: "Rename", onSelect: () => { setError(null); setMode({ kind: "rename" }); } },
                  { label: "Duplicate", onSelect: async () => {
                    const r = await run(`/templates/${t.id}/duplicate`, `Copied ${t.name}.`) as { template?: { id: string }; notice?: string | null } | null;
                    // priced in another company than the source's: say so (C)
                    if (r?.notice) toast(r.notice);
                    if (r?.template) navigate(`/builder?template=${r.template.id}`);
                  } },
                  { label: "Archive", onSelect: () => setMode({ kind: "archive" }) },
                ]} />
              </div>
            )}
          </div>
        )}
      </div>
    </article>
  );
}

function RenameForm({ t, busy, error, onSave, onCancel }: { t: TemplateDto; busy: boolean; error: string | null; onSave: (name: string) => void; onCancel: () => void }) {
  const input = useRef<HTMLInputElement>(null);
  useEffect(() => { input.current?.select(); }, []);
  const submit = (e: FormEvent<HTMLFormElement>) => { e.preventDefault(); onSave(String(new FormData(e.currentTarget).get("name") ?? "")); };
  return (
    <form className="stackgap stackgap--sm" onSubmit={submit} noValidate aria-label={`Rename ${t.name}`}>
      <Field label="Name" error={error}><input ref={input} name="name" className="input" defaultValue={t.name} maxLength={80} /></Field>
      <div className="actions">
        <button type="submit" className="btn btn--sm btn--primary" disabled={busy}>Save name</button>
        <button type="button" className="btn btn--sm" onClick={onCancel}>Cancel</button>
      </div>
    </form>
  );
}

function NewTemplateForm({ pricing, onCancel }: { pricing: PricingDto | null; onCancel: () => void }) {
  const [, navigate] = useLocation();
  const qc = useQueryClient();
  const headingId = useId();
  const [source, setSource] = useState("purchased");
  const [acres, setAcres] = useState("3");
  const [tpa, setTpa] = useState("3.2");
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const estate = source === "estate";
  const derived = Number(acres) * Number(tpa);
  const submit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const f = new FormData(e.currentTarget);
    const n = (k: string) => (f.get(k) === null || f.get(k) === "" ? undefined : Number(f.get(k)));
    setBusy(true); setError(null);
    try {
      const r = await api<{ template: { id: string } }>("/templates/drafts", { body: {
        name: f.get("name"), wineType: f.get("wineType"), agingRoute: f.get("agingRoute"), fruitSource: source,
        baseCases: n("baseCases"), ...(pricing?.show ? { pricingCompanyId: f.get("pricingCompanyId") } : {}), ...(estate ? { baseAcres: n("baseAcres"), baseTonsPerAcre: n("baseTonsPerAcre") } : { baseTons: n("baseTons") }),
      } });
      void qc.invalidateQueries({ queryKey: ["templates"] });
      navigate(`/builder?template=${r.template.id}`);
    } catch (err) { setError(message(err)); setBusy(false); }
  };
  return (
    <form className="card tplnew" onSubmit={submit} noValidate aria-labelledby={headingId}>
      <div className="card__h"><div>
        <h2 className="card__t" id={headingId}>New template</h2>
        <p className="card__d">A draft opens in the Program builder. Add the activities, confirm their sizes, and the hours fill in from the team's estimates.</p>
      </div></div>
      <div className="fields">
        <Field label="Name"><input name="name" className="input" placeholder="White, whole cluster" maxLength={80} required autoFocus /></Field>
        <Field label="Wine type">
          <select name="wineType" className="input" defaultValue="white">{["red", "white", "rose", "sparkling"].map(w => <option key={w} value={w}>{WINE_LABEL[w]}</option>)}</select>
        </Field>
        <Field label="Route">
          <select name="agingRoute" className="input" defaultValue="stainless">{["none", "stainless", "barrel"].map(r => <option key={r} value={r}>{ROUTE_LABEL[r]}</option>)}</select>
        </Field>
        <Field label="Fruit source">
          <select name="fruitSource" className="input" value={source} onChange={e => setSource(e.target.value)}>
            <option value="purchased">Purchased</option><option value="estate">Estate</option>
          </select>
        </Field>
        {pricing?.show && (
          <Field label="Priced in" hint="Its pools and rates price the standard cost per case.">
            <select name="pricingCompanyId" className="input" defaultValue={pricing.defaultCompanyId ?? undefined}>
              {pricing.companies.map(co => <option key={co.id} value={co.id}>{co.name}</option>)}
            </select>
          </Field>
        )}
        {estate ? (
          <>
            <Field label="Acres"><input name="baseAcres" className="input tnum" type="number" min="0" step="any" inputMode="decimal" value={acres} onChange={e => setAcres(e.target.value)} /></Field>
            <Field label="Tons per acre"><input name="baseTonsPerAcre" className="input tnum" type="number" min="0" step="any" inputMode="decimal" value={tpa} onChange={e => setTpa(e.target.value)} /></Field>
            <p className="tplderived" aria-live="polite">{Number.isFinite(derived) && derived > 0 ? `${fmt(Math.round(derived * 1000) / 1000)} tons` : "Tons follow from acres and tons per acre"}</p>
          </>
        ) : (
          <Field label="Tons"><input name="baseTons" className="input tnum" type="number" min="0" step="any" inputMode="decimal" defaultValue="10" /></Field>
        )}
        <Field label="Target cases"><input name="baseCases" className="input tnum" type="number" min="0" step="1" inputMode="numeric" defaultValue="600" /></Field>
      </div>
      <div className="card__c stackgap stackgap--sm">
        {error && <p className="field__error" role="alert">{error}</p>}
        <div className="actions">
          <button type="submit" className="btn btn--primary" disabled={busy}>{busy ? "Creating…" : "Create and build"}</button>
          <button type="button" className="btn" onClick={onCancel}>Cancel</button>
        </div>
      </div>
    </form>
  );
}
